import {
  type BookDetail,
  buildPath,
  type Casting,
  type ChapterProposal,
  type NodesResponse,
  type Sample,
  type SampleRequest,
  type SampleSource,
  type VoiceId,
  type VoicesResponse,
} from "@contract";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { api } from "../../../api/fetcher";
import { queryKeys } from "../../../api/keys";
import { POLL_INTERVAL_MS } from "../../../api/poll";
import { usePlayback } from "../../../components/audio/AudioProvider";
import { useBookDetail } from "../bookContext";
import { samplePassage } from "./samplePassage";

export function useCasting() {
  const book = useBookDetail();
  const client = useQueryClient();
  const language = book.language ?? book.languageDetected;
  const voices = useQuery({
    queryKey: queryKeys.voices(language ?? "unknown"),
    queryFn: ({ signal }) => api<VoicesResponse>(`/voices?language=${language}`, { signal }),
    enabled: language !== null,
    staleTime: 60_000,
  });
  const casting = useQuery({
    queryKey: queryKeys.casting(book.id),
    queryFn: ({ signal }) =>
      api<Casting>(buildPath("/books/:id/casting", { id: book.id }), { signal }),
  });
  // biome-ignore lint/correctness/useExhaustiveDependencies: the Book revision deliberately refreshes this derived view.
  useEffect(() => {
    void client.invalidateQueries({ queryKey: queryKeys.casting(book.id) });
  }, [book.id, book.updatedAt, client]);
  const save = useMutation({
    mutationFn: (voiceId: VoiceId) =>
      api<Casting>(buildPath("/books/:id/casting", { id: book.id }), {
        method: "PUT",
        body: { voiceId },
      }),
    retry: false,
    onSuccess: (result) => client.setQueryData(queryKeys.casting(book.id), result),
    onSettled: () => client.invalidateQueries({ queryKey: queryKeys.book(book.id) }),
  });
  return { voices, casting, save, language };
}

export interface SampleParagraph {
  readonly source: SampleSource;
  readonly text: string;
}

/** A stable manuscript passage keeps voice comparisons intact while scripts are prepared. */
async function readSampleParagraph(
  bookId: string,
  chapterId: string,
  signal: AbortSignal,
): Promise<SampleParagraph | null> {
  const proposal = await api<ChapterProposal>(buildPath("/books/:id/proposal", { id: bookId }), {
    signal,
  });
  if (proposal.status !== "ready") return null;
  const chapter = proposal.chapters.find((row) => row.id === chapterId);
  if (!chapter) return null;
  for (let from = chapter.nodeStart; from < chapter.nodeEnd; from += 2000) {
    const to = Math.min(from + 2000, chapter.nodeEnd);
    const page = await api<NodesResponse>(
      `${buildPath("/books/:id/nodes", { id: bookId })}?from=${from}&to=${to}`,
      { signal },
    );
    const paragraph = samplePassage(page.nodes, chapter.title);
    if (paragraph)
      return {
        source: { kind: "node", chapterId: chapter.id, nodeId: paragraph.id },
        text: paragraph.text,
      };
  }
  return null;
}

export function useSampleParagraph(chapterId: string | undefined) {
  const book = useBookDetail();
  return useQuery({
    queryKey: queryKeys.sampleSource(book.id, chapterId ?? "", "manuscript"),
    queryFn: ({ signal }) => readSampleParagraph(book.id, chapterId as string, signal),
    enabled: chapterId !== undefined,
    gcTime: 30_000,
  });
}

export const sampleUrl = (sampleId: string) =>
  `/api${buildPath("/samples/:id/audio", { id: sampleId })}`;

/** Local history records only Samples the Author requested; no invented "heard" state. */
export function useVoiceSamples(
  book: BookDetail,
  paragraph: SampleParagraph | null | undefined,
  voiceId: VoiceId | undefined,
  voiceRate?: number | null,
) {
  const client = useQueryClient();
  const playback = usePlayback();
  const [known, setKnown] = useState<Record<string, Sample>>({});
  const base = JSON.stringify([
    book.id,
    paragraph?.source,
    paragraph?.text,
    book.ttsModel,
    book.stability,
  ]);
  const identity = `${base}:${voiceId ?? ""}`;
  const latestIdentity = useRef(identity);
  const autoPlay = useRef<string | null>(null);
  if (latestIdentity.current !== identity) {
    latestIdentity.current = identity;
    autoPlay.current = null;
  }
  const sample = known[identity];
  const remember = (key: string, value: Sample) =>
    setKnown((old) => (old[key] === value ? old : { ...old, [key]: value }));
  const request = useMutation({
    mutationFn: ({ body }: { key: string; body: SampleRequest }) =>
      api<Sample>(buildPath("/books/:id/samples", { id: book.id }), { method: "POST", body }),
    retry: false,
    onSuccess: (value, variables) => remember(variables.key, value),
  });
  const poll = useQuery({
    queryKey: queryKeys.sample(sample?.id ?? ""),
    queryFn: ({ signal }) =>
      api<Sample>(buildPath("/samples/:id", { id: sample?.id as string }), { signal }),
    enabled: sample?.status === "rendering",
    refetchInterval: (query) =>
      query.state.data?.status === "rendering" ? POLL_INTERVAL_MS : false,
    retry: false,
  });
  useEffect(() => {
    if (!poll.data || poll.data.id !== sample?.id) return;
    setKnown((old) =>
      old[identity] === poll.data ? old : { ...old, [identity]: poll.data as Sample },
    );
  }, [poll.data, sample?.id, identity]);
  useEffect(() => {
    if (sample?.status !== "cached" || autoPlay.current !== identity) return;
    autoPlay.current = null;
    playback.play(sample.id, sampleUrl(sample.id));
  }, [sample, identity, playback.play]);
  useEffect(
    () => () => {
      autoPlay.current = null;
    },
    [],
  );

  const play = () => {
    if (!paragraph || !voiceId || request.isPending) return;
    if (sample?.status === "cached") {
      if (playback.activeId === sample.id && playback.status === "playing") playback.pause();
      else playback.play(sample.id, sampleUrl(sample.id));
      return;
    }
    autoPlay.current = identity;
    if (sample?.status === "rendering") {
      void poll.refetch();
      return;
    }
    request.mutate({
      key: identity,
      body: {
        source: paragraph.source,
        voiceId,
        ...(voiceRate != null ? { expectedVoiceRate: voiceRate } : {}),
      },
    });
  };
  useEffect(() => {
    if (sample?.status === "failed")
      void client.invalidateQueries({
        queryKey: queryKeys.voices(book.language ?? book.languageDetected ?? "unknown"),
      });
  }, [sample?.status, client, book.language, book.languageDetected]);
  const active = sample !== undefined && playback.activeId === sample.id;
  const status =
    request.isPending || sample?.status === "rendering"
      ? "loading"
      : (request.isError && request.variables?.key === identity) ||
          poll.isError ||
          sample?.status === "failed"
        ? "failed"
        : active
          ? playback.status
          : "idle";
  return {
    sample,
    status,
    play,
    error: (request.variables?.key === identity ? request.error : null) ?? poll.error,
    busy: request.isPending,
    history: Object.entries(known)
      .filter(([key]) => key.startsWith(`${base}:`))
      .map(([, value]) => value),
    playback,
    active,
  };
}
